/**
 * MSE Tier Preview (Farbgel)
 * Zeigt aktuelle und nächste Staffelpreise anhand der Farbgel-Menge im Warenkorb.
 */

const MSE_DEFAULT_FARBGEL_TIERS = [
  { qty: 1, price_cents: 450 },
  { qty: 100, price_cents: 356 },
  { qty: 200, price_cents: 320 },
  { qty: 300, price_cents: 275 }
];

async function mseFetchCart() {
  const res = await fetch('/cart.js', { credentials: 'same-origin' });
  if (!res.ok) throw new Error('Cart fetch failed');
  return await res.json();
}

function mseNormalize(s) {
  return String(s || '').toLowerCase().trim();
}

function mseIsFarbgelItem(item) {
  const pt = mseNormalize(item.product_type);
  const title = mseNormalize(item.product_title || item.title);
  const handle = mseNormalize(item.handle);
  const url = mseNormalize(item.url);

  return (
    /farb\s*gel/.test(title) ||
    /farbgel/.test(title) ||
    /color\s*gel/.test(title) ||
    /colour\s*gel/.test(title) ||
    /farb/.test(pt) ||
    /color\s*gel/.test(pt) ||
    /colour\s*gel/.test(pt) ||
    /farbgel/.test(handle) ||
    /color-gel/.test(handle) ||
    /colour-gel/.test(handle) ||
    /farbgel/.test(url) ||
    /color-gel/.test(url) ||
    /colour-gel/.test(url)
  );
}

function mseCountFarbgel(cart) {
  let qty = 0;

  for (const item of cart.items || []) {
    if (mseIsFarbgelItem(item)) {
      qty += Number(item.quantity || 0);
    }
  }

  return qty;
}

/*
 * MSE_TIER_VIRTUAL_PREORDER_V109_20260917
 *
 * Im Preorder-Testmodus wird die Staffel aus den
 * servervalidierten virtuellen Wunschmengen berechnet.
 */
function mseVirtualPreorderQuantity() {
  try {
    if (
      sessionStorage.getItem(
        'mse_preorder_virtual_v108_test'
      ) !== '1'
    ) {
      return null;
    }

    const stored = JSON.parse(
      sessionStorage.getItem(
        'mse_preorder_virtual_v108_items'
      ) || '{}'
    );

    if (
      !stored
      || typeof stored !== 'object'
      || Array.isArray(stored)
    ) {
      return 0;
    }

    let total = 0;

    for (const entry of Object.values(stored)) {
      const sku =
        String(entry?.sku || '').trim();

      const ordered =
        Number.parseInt(
          entry?.preview?.summary?.ordered_quantity,
          10
        );

      const validSku =
        /^150(?:1[0-9]{3}|5[0-9]{3}|5P[0-9]{2}|5S[0-9]{2})$/i
          .test(sku);

      const validated =
        entry?.preview?.ok === true
        && entry?.preview?.preview_stateless === true
        && entry?.preview?.checkout_enabled === false;

      if (
        validSku
        && validated
        && Number.isInteger(ordered)
        && ordered > 0
      ) {
        total += ordered;
      }
    }

    return total;

  } catch (error) {
    console.warn(
      'MSE_TIER_VIRTUAL_PREORDER_V109_20260917',
      error
    );

    return 0;
  }
}

function mseTierLocale() {
  const raw = String(document.documentElement.lang || 'de').toLowerCase();
  const code = raw.split('-')[0];
  return ['de', 'en', 'fr', 'vi'].includes(code) ? code : 'de';
}

const MSE_TIER_I18N = {
  de: {
    currentTier: '✅ Aktuelle Staffel:',
    from: 'ab',
    unitOne: 'Stück',
    unitMany: 'Stück',
    priceFrom: 'Dein Preis ab {qty} {unit}:',
    net: 'netto',
    gross: 'brutto',
    singlePrice: 'Einzelpreis:',
    savings: '{percent} % günstiger als Einzelkauf',
    volumePrice: 'Mengenpreis',
    reached: 'Diese Staffel ist aktuell erreicht.',
    missing: 'Nur noch {missing} Stück bis zur nächsten Staffel',
    highest: 'Du hast bereits die höchste Staffel erreicht.',
    virtualHint: 'Hinweis: Wird anhand deiner Farbgel-Wunschmenge berechnet.'
  },
  en: {
    currentTier: '✅ Current tier:',
    from: 'from',
    unitOne: 'pc',
    unitMany: 'pcs',
    priceFrom: 'Your price from {qty} {unit}:',
    net: 'net',
    gross: 'incl. VAT',
    singlePrice: 'Single price:',
    savings: '{percent}% less than buying individually',
    volumePrice: 'Bulk price',
    reached: 'You’ve reached this tier.',
    missing: 'Only {missing} more to reach the next tier',
    highest: 'You’ve already reached the best price tier.',
    virtualHint: 'Note: Calculated from your requested color-gel quantity.'
  },
  fr: {
    currentTier: '✅ Tarif actuel :',
    from: 'à partir de',
    unitOne: 'pièce',
    unitMany: 'pièces',
    priceFrom: 'Votre prix dès {qty} {unit} :',
    net: 'HT',
    gross: 'TTC',
    singlePrice: 'Prix unitaire :',
    savings: '{percent} % de moins que le prix unitaire',
    volumePrice: 'Prix par quantité',
    reached: 'Ce tarif est déjà atteint.',
    missing: 'Plus que {missing} pour atteindre le tarif suivant',
    highest: 'Vous avez déjà atteint le meilleur tarif.',
    virtualHint: 'Remarque : calculé selon la quantité de gels couleur souhaitée.'
  },
  vi: {
    currentTier: '✅ Mức giá hiện tại:',
    from: 'từ',
    unitOne: 'sản phẩm',
    unitMany: 'sản phẩm',
    priceFrom: 'Giá từ {qty} {unit}:',
    net: 'chưa VAT',
    gross: 'đã gồm VAT',
    singlePrice: 'Giá lẻ:',
    savings: 'Tiết kiệm {percent} % so với mua lẻ',
    volumePrice: 'Giá theo số lượng',
    reached: 'Bạn đã đạt mức giá này.',
    missing: 'Còn {missing} sản phẩm để đạt mức giá tiếp theo',
    highest: 'Bạn đã đạt mức giá tốt nhất.',
    virtualHint: 'Lưu ý: Giá được tính theo số lượng gel màu bạn muốn đặt.'
  }
};

function mseTierText() {
  return MSE_TIER_I18N[mseTierLocale()] || MSE_TIER_I18N.de;
}

function mseTierInterpolate(text, vars) {
  return String(text).replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ''));
}

function mseFormatMoneyDE(cents) {
  const locales = {
    de: 'de-DE',
    en: 'en-GB',
    fr: 'fr-FR',
    vi: 'vi-VN'
  };

  return (Number(cents || 0) / 100).toLocaleString(
    locales[mseTierLocale()] || 'de-DE',
    {
      style: 'currency',
      currency: 'EUR'
    }
  );
}

function mseGrossToNetCents(grossCents) {
  return Math.max(0, Math.round((Number(grossCents || 0) * 100) / 119));
}

function mseGetTiers(el) {
  let tiers = [];

  try {
    tiers = JSON.parse(el.getAttribute('data-tiers') || '[]');
  } catch (e) {
    tiers = [];
  }

  if (!Array.isArray(tiers) || !tiers.length) {
    tiers = MSE_DEFAULT_FARBGEL_TIERS;
  }

  return tiers
    .map(t => ({
      qty: Number(t.qty || 0),
      price_cents: Number(t.price_cents || 0),
      net_cents: Number(t.net_cents || 0)
    }))
    .filter(t => t.qty > 0 && t.price_cents > 0)
    .sort((a, b) => a.qty - b.qty);
}

function mseCurrentTier(totalQty, tiers) {
  return tiers.filter(t => totalQty >= t.qty).pop() || null;
}

function mseNextTiers(totalQty, tiers) {
  return tiers.filter(t => totalQty < t.qty).slice(0, 2);
}

function mseTierLine(tier, totalQty, isActive, baseNetCents = 0) {
  const qty = tier.qty;
  const grossCents = tier.price_cents;
  const netCents = tier.net_cents > 0 ? tier.net_cents : mseGrossToNetCents(grossCents);
  const missing = Math.max(0, qty - totalQty);
  const copy = mseTierText();
  const unit = qty === 1 ? copy.unitOne : copy.unitMany;
  const headline = `${isActive ? copy.currentTier : copy.from} ${qty} ${unit}`;
  const savings = baseNetCents > netCents ? Math.round((1 - netCents / baseNetCents) * 100) : 0;
  const featured = qty === 300 && baseNetCents > 0;

  return `
    <div style="padding:14px 14px;border-radius:12px;border:${featured ? '2px solid #6857b2' : (isActive ? '1px solid #16a34a' : '1px solid rgba(104,87,178,.20)')};background:${featured ? '#f1edff' : (isActive ? '#f0fdf4' : '#fff')};">
      <div style="display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:6px;font-weight:900;">
        <span>${headline}</span>
        ${savings ? `<span style="font-size:11px;line-height:1.3;padding:4px 7px;border-radius:6px;background:#6857b2;color:#fff;">${mseTierInterpolate(copy.savings, {percent:savings})}</span>` : ''}
      </div>
      <div style="display:flex;flex-wrap:wrap;align-items:baseline;gap:5px 8px;margin-top:8px;">
        <strong style="font-size:clamp(24px,5vw,30px);font-weight:900;line-height:1.15;color:#6857b2;">${mseFormatMoneyDE(netCents)} <span style="font-size:13px;">${copy.net}</span></strong>
        <small style="font-size:11px;color:#555;">(${mseFormatMoneyDE(grossCents)} ${copy.gross})</small>
      </div>
      <div style="color:#555;font-size:12px;margin-top:7px;">${missing === 0 ? copy.reached : mseTierInterpolate(copy.missing, {missing})}</div>
    </div>
  `;
}

function mseUpdateVolumeOffer(el, tiers) {
  const scope = el.closest('.product-info');
  const anchor = scope?.querySelector('.product-info__price');
  if (!anchor) return;
  let offer = scope.querySelector('.mse-volume-price-offer');
  const bulk = tiers.find(t => t.qty === 300);
  const base = tiers.find(t => t.qty === 1);
  const eligible = el.getAttribute('data-customer-mode') !== 'export' && !el.hidden && bulk && base;
  if (!eligible) { if (offer) offer.hidden = true; return; }
  if (!offer) {
    offer = document.createElement('div');
    offer.className = 'mse-volume-price-offer';
    offer.style.cssText = 'margin-top:10px;padding:12px 14px;border:2px solid #6857b2;border-radius:12px;background:#f1edff;';
    anchor.insertAdjacentElement('afterend', offer);
  }
  const copy = mseTierText();
  const net = bulk.net_cents > 0 ? bulk.net_cents : mseGrossToNetCents(bulk.price_cents);
  const baseNet = base.net_cents > 0 ? base.net_cents : mseGrossToNetCents(base.price_cents);
  const savings = Math.max(0, Math.round((1 - net / baseNet) * 100));
  offer.hidden = false;
  offer.innerHTML = `
    <div style="display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:6px;font-size:13px;font-weight:800;color:#463681;">
      <span>${copy.volumePrice} · ${copy.from} ${bulk.qty} ${copy.unitMany}</span>
      <span style="font-size:11px;">${mseTierInterpolate(copy.savings, {percent:savings})}</span>
    </div>
    <div style="display:flex;flex-wrap:wrap;align-items:baseline;gap:6px;margin-top:4px;">
      <strong style="font-size:28px;font-weight:900;line-height:1.2;color:#6857b2;">${mseFormatMoneyDE(net)} <span style="font-size:13px;">${copy.net}</span></strong>
      <small style="font-size:11px;color:#555;">(${mseFormatMoneyDE(bulk.price_cents)} ${copy.gross})</small>
    </div>
  `;
}

function mseRenderTierPreview(el, totalQty, tiers) {
  const rows = el.querySelector('.mse-tier-preview__rows');
  if (!rows) return;

  const header = rows.previousElementSibling;
  const singleTier = tiers.find(t => t.qty === 1);
  mseUpdateVolumeOffer(el, tiers);
  if (header && singleTier && el.getAttribute('data-customer-mode') !== 'export') {
    let single = header.querySelector('.mse-tier-preview__single-price');
    if (!single) {
      single = document.createElement('span');
      single.className = 'mse-tier-preview__single-price';
      single.style.cssText = 'display:inline-flex;flex-wrap:wrap;align-items:baseline;gap:4px;margin-left:10px;font-size:13px;font-weight:400;';
      header.append(single);
    }
    const net = singleTier.net_cents > 0 ? singleTier.net_cents : mseGrossToNetCents(singleTier.price_cents);
    const copy = mseTierText();
    single.innerHTML = `<span>${copy.singlePrice}</span> <strong>${mseFormatMoneyDE(net)} ${copy.net}</strong> <small style="font-size:11px;opacity:.8;">(${mseFormatMoneyDE(singleTier.price_cents)} ${copy.gross})</small>`;
  } else {
    header?.querySelector('.mse-tier-preview__single-price')?.remove();
  }

  const current = mseCurrentTier(totalQty, tiers);
  const next = mseNextTiers(totalQty, tiers);

  let html = '';

  if (el.getAttribute('data-customer-mode') === 'export') {
    if (current) html += mseTierLine(current, totalQty, true);
    if (next.length) html += next.map(t => mseTierLine(t, totalQty, false)).join('');
  } else {
    const volumeTiers = tiers.filter(t => t.qty > 1);
    const visible = current && volumeTiers.length >= 3 && current.qty > volumeTiers[2].qty
      ? [current, ...next]
      : volumeTiers.slice(0, 3);
    const baseNet = singleTier ? (singleTier.net_cents > 0 ? singleTier.net_cents : mseGrossToNetCents(singleTier.price_cents)) : 0;
    html = visible.map(t => mseTierLine(t, totalQty, current?.qty === t.qty, baseNet)).join('');
  }

  if (!html) {
    html = `<div style="font-weight:700;">${mseTierText().highest}</div>`;
  }

  rows.innerHTML = html;

  /*
   * MSE_TIER_VIRTUAL_NOTE_V110_20260917
   * Im Preorder-Testmodus klarstellen, dass die
   * virtuelle Wunschmenge die Staffel bestimmt.
   */
  const hint =
    rows.nextElementSibling;

  if (hint) {
    if (!hint.dataset.mseV110OriginalText) {
      hint.dataset.mseV110OriginalText =
        hint.textContent.trim();
    }

    const virtualQty =
      mseVirtualPreorderQuantity();

    if (virtualQty !== null) {
      hint.textContent =
        mseTierText().virtualHint;
    } else {
      hint.textContent =
        hint.dataset.mseV110OriginalText;
    }
  }
}

async function mseInitTierPreview() {
  const els = document.querySelectorAll('.mse-tier-preview');
  if (!els.length) return;

  const virtualQty =
    mseVirtualPreorderQuantity();

  let totalQty = 0;

  if (virtualQty !== null) {
    totalQty = virtualQty;
  } else {
    let cart;

    try {
      cart = await mseFetchCart();
    } catch (e) {
      cart = { items: [] };
    }

    totalQty =
      mseCountFarbgel(cart);
  }

  els.forEach(el => {
    const tiers = mseGetTiers(el);

    mseRenderTierPreview(
      el,
      totalQty,
      tiers
    );
  });
}

document.addEventListener('DOMContentLoaded', mseInitTierPreview);

document.addEventListener(
  'mse:preorder-virtual:updated',
  mseInitTierPreview
);

document.addEventListener('cart:updated', mseInitTierPreview);
document.addEventListener('cart:refresh', mseInitTierPreview);
document.addEventListener('cart:change', mseInitTierPreview);

document.addEventListener('click', function () {
  setTimeout(mseInitTierPreview, 500);
});

/* MSE_TIER_VARIANT_VISIBILITY_1505001_V1_20260914 */
(() => {
  const mse001Path =
    '/products/001-effekt-farbgel-color-gel-5ml-dezent-schimmernder-nude-ton';

  function isMse001Page() {
    return window.location.pathname === mse001Path ||
           window.location.pathname === mse001Path + '/';
  }

  function currentSku() {
    return (
      document.querySelector('.product-sku__value')?.textContent || ''
    ).trim();
  }

  function applyTierVisibility(sku) {
    if (!isMse001Page()) return;

    const show = String(sku || '').trim() === '1505001';

    document.querySelectorAll('.mse-tier-preview').forEach((el) => {
      el.hidden = !show;
    });
  }

  function init() {
    applyTierVisibility(currentSku());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('on:variant:change', (evt) => {
    const sku =
      evt.detail?.variant?.sku ||
      currentSku();

    applyTierVisibility(sku);
  });

  window.addEventListener('pageshow', init);
})();
/* /MSE_TIER_VARIANT_VISIBILITY_1505001_V1_20260914 */


/* MSE_TIER_ALL_1505_1501_JS_V1_20260914 */
(() => {
  function isFarbgel5mlSku(sku) {
    sku = String(sku || '').trim();
    return sku.startsWith('1505') || sku.startsWith('1501');
  }

  function currentSku() {
    return (
      document.querySelector('.product-sku__value')?.textContent || ''
    ).trim();
  }

  function apply(sku) {
    const show = isFarbgel5mlSku(sku);

    document.querySelectorAll('.mse-tier-preview').forEach((el) => {
      el.hidden = !show;
    });
  }

  function init() {
    apply(currentSku());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once:true });
  } else {
    init();
  }

  document.addEventListener('on:variant:change', (evt) => {
    apply(evt.detail?.variant?.sku || currentSku());
  });

  window.addEventListener('pageshow', init);
})();
/* /MSE_TIER_ALL_1505_1501_JS_V1_20260914 */

document.addEventListener('on:variant:change', () => { setTimeout(mseInitTierPreview, 0); });
